
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const questions = [
  {
    question: "What's the occasion?",
    options: [
      { icon: 'ri-heart-fill', text: 'Romantic Date', value: 'romantic' },
      { icon: 'ri-cake-3-fill', text: 'Birthday', value: 'birthday' },
      { icon: 'ri-gift-fill', text: 'Anniversary', value: 'anniversary' },
      { icon: 'ri-star-fill', text: 'Just Because', value: 'justbecause' }
    ]
  },
  {
    question: "What's your budget range?",
    options: [
      { icon: 'ri-money-dollar-circle-line', text: 'Under $200', value: 'budget1' },
      { icon: 'ri-money-dollar-circle-fill', text: '$200 - $400', value: 'budget2' },
      { icon: 'ri-vip-crown-fill', text: '$400 - $600', value: 'budget3' },
      { icon: 'ri-vip-diamond-fill', text: '$600+', value: 'budget4' }
    ]
  },
  {
    question: "What's the vibe you're going for?",
    options: [
      { icon: 'ri-heart-3-fill', text: 'Intimate & Cozy', value: 'intimate' },
      { icon: 'ri-sparkling-fill', text: 'Bold & Dramatic', value: 'dramatic' },
      { icon: 'ri-flower-fill', text: 'Elegant & Classic', value: 'elegant' },
      { icon: 'ri-palette-fill', text: 'Fun & Playful', value: 'playful' }
    ]
  },
  {
    question: "Where will the surprise take place?",
    options: [
      { icon: 'ri-home-heart-fill', text: 'At Home', value: 'home' },
      { icon: 'ri-restaurant-fill', text: 'Restaurant', value: 'restaurant' },
      { icon: 'ri-hotel-fill', text: 'Hotel Room', value: 'hotel' },
      { icon: 'ri-landscape-fill', text: 'Outdoor', value: 'outdoor' }
    ]
  },
  {
    question: "How much time do we have to set up?",
    options: [
      { icon: 'ri-time-fill', text: 'Less than 1 hour', value: 'quick' },
      { icon: 'ri-timer-fill', text: '1-2 hours', value: 'medium' },
      { icon: 'ri-hourglass-fill', text: '2-4 hours', value: 'extended' },
      { icon: 'ri-calendar-fill', text: 'Full day', value: 'fullday' }
    ]
  }
];

const recommendations = {
  romantic: {
    name: 'Intimate Evening',
    description: 'Perfect for creating a cozy, romantic atmosphere with candles, roses, and fairy lights.',
    price: '299',
    gradient: 'from-pink to-coral',
    image: 'https://public.readdy.ai/ai/img_res/20fc0f5b6ff4455c6f4f101c51bb2eb9.jpg'
  },
  birthday: {
    name: 'Sweet Surprise',
    description: 'A delightful setup with balloons, flowers, and festive decorations to celebrate their special day.',
    price: '199',
    gradient: 'from-purple to-pink',
    image: 'https://readdy.ai/api/search-image?query=birthday%20surprise%20decoration%20with%20colorful%20balloons%20flowers%20and%20festive%20elements%20on%20clean%20background%20celebration%20setup%20professional%20photography&width=600&height=400&seq=rec002&orientation=landscape'
  },
  anniversary: {
    name: 'Grand Gesture',
    description: 'An elegant celebration with premium florals, balloon arch, and luxury touches.',
    price: '599',
    gradient: 'from-coral to-purple',
    image: 'https://readdy.ai/api/search-image?query=anniversary%20surprise%20decoration%20with%20elegant%20balloon%20arch%20luxury%20flowers%20and%20romantic%20lighting%20celebration%20setup%20professional%20photography&width=600&height=400&seq=rec003&orientation=landscape'
  },
  justbecause: {
    name: 'Intimate Evening',
    description: 'A thoughtful surprise to show you care, with beautiful decorations and personal touches.',
    price: '299',
    gradient: 'from-pink to-coral',
    image: 'https://readdy.ai/api/search-image?query=surprise%20decoration%20with%20flowers%20balloons%20and%20elegant%20touches%20on%20soft%20background%20romantic%20setup%20professional%20photography&width=600&height=400&seq=rec004&orientation=landscape'
  }
};

export default function Quiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleAnswer = (value: string) => {
    setSelectedOption(value);
    setTimeout(() => {
      const newAnswers = [...answers, value];
      setAnswers(newAnswers);
      
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        setShowResults(true);
      }
    }, 300);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
    setSelectedOption(null);
  };

  const getRecommendation = () => {
    const occasion = answers[0];
    return recommendations[occasion as keyof typeof recommendations] || recommendations.romantic;
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="quiz" className="py-20 bg-gradient-to-br from-coral via-pink to-purple relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <AnimatePresence mode="wait">
          {!showResults ? (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-coral to-pink rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-question-mark text-white text-3xl"></i>
                </div>
                <h2 className="font-serif font-bold text-3xl md:text-4xl text-charcoal mb-3">
                  Discover Your Surprise Style
                </h2>
                <p className="text-gray-600">
                  Answer 5 quick questions to get personalized recommendations
                </p>
              </div>

              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-500">
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                  <div className="flex gap-2">
                    {questions.map((_, idx) => (
                      <div
                        key={idx}
                        className={`h-2 rounded-full transition-all ${
                          idx <= currentQuestion ? 'w-8 bg-gradient-to-r from-coral to-pink' : 'w-2 bg-gray-200'
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-charcoal mb-8 text-center">
                  {questions[currentQuestion].question}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  {questions[currentQuestion].options.map((option, idx) => (
                    <motion.button
                      key={idx}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleAnswer(option.value)}
                      className={`p-6 rounded-2xl border-2 transition-all cursor-pointer ${
                        selectedOption === option.value
                          ? 'bg-gradient-to-br from-coral to-pink text-white border-transparent'
                          : 'bg-white border-gray-200 hover:border-coral'
                      }`}
                    >
                      <i className={`${option.icon} text-3xl mb-3 block`}></i>
                      <div className="font-medium text-sm">{option.text}</div>
                    </motion.button>
                  ))}
                </div>
              </div>

              {currentQuestion > 0 && (
                <button
                  onClick={() => {
                    setCurrentQuestion(currentQuestion - 1);
                    setAnswers(answers.slice(0, -1));
                  }}
                  className="text-gray-500 hover:text-charcoal transition-colors text-sm cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1"></i> Back
                </button>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring' }}
                className="text-center mb-8"
              >
                <div className="text-6xl mb-4">🎉</div>
                <h2 className="font-serif font-bold text-3xl md:text-4xl text-charcoal mb-3">
                  Your Perfect Match
                </h2>
                <p className="text-gray-600">
                  Based on your answers, we recommend:
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`bg-gradient-to-br ${getRecommendation().gradient} rounded-2xl p-8 text-white mb-8`}
              >
                <div className="w-full h-64 mb-6 rounded-xl overflow-hidden">
                  <img 
                    src={getRecommendation().image} 
                    alt={getRecommendation().name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <h3 className="font-serif font-bold text-3xl mb-3">
                  {getRecommendation().name}
                </h3>
                <p className="text-white/90 mb-6">
                  {getRecommendation().description}
                </p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm opacity-80">Starting from</div>
                    <div className="text-4xl font-bold">${getRecommendation().price}</div>
                  </div>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="px-8 py-3 bg-white text-charcoal font-semibold rounded-full hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
                  >
                    Book Your Surprise
                  </button>
                </div>
              </motion.div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={resetQuiz}
                  className="px-6 py-3 border-2 border-gray-300 text-charcoal font-medium rounded-full hover:border-coral transition-colors cursor-pointer whitespace-nowrap"
                >
                  Retake Quiz
                </button>
                <button
                  onClick={() => scrollToSection('packages')}
                  className="px-6 py-3 bg-gradient-to-r from-coral to-pink text-white font-medium rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  View All Packages
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
